import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ReportMultiSelect } from '../../components/reports/ReportMultiSelect';
import { ReportExportToolbar } from '../../components/reports/ReportExportToolbar';
import { ReportMetric } from '../../components/reports/ReportBadges';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import { setFlashToast } from '../../utils/flashToast';
import { defaultDashboardDateRange, todayIsoDate, validateReportDateRange } from '../../utils/reportDates';
import {
  downloadReportBlob,
  exportWorkflowPerformanceReport,
  searchWorkflowPerformanceReport,
  type WorkflowPerformanceReportFilters,
} from '../../services/workflowPerformanceReports';
import type { WorkflowPerformanceRow } from '../../data/reports';
import { ReportFilterActions } from './ReportFilterActions';
import { useWorkflowPerformanceLookups } from './useWorkflowPerformanceLookups';

type Filters = {
  workflowTemplates: string[];
  fileCategories: string[];
  subFileCategories: string[];
  workflowStageIds: string[];
  allocationOfficers: string[];
  workflowTemplateStatuses: string[];
  from: string;
  to: string;
};

function createEmptyFilters(): Filters {
  const { from, to } = defaultDashboardDateRange(1);
  return {
    workflowTemplates: [],
    fileCategories: [],
    subFileCategories: [],
    workflowStageIds: [],
    allocationOfficers: [],
    workflowTemplateStatuses: [],
    from,
    to,
  };
}

function createEmptyFilterIds(): WorkflowPerformanceReportFilters {
  const { from, to } = defaultDashboardDateRange(1);
  return {
    workflowTemplateIds: [],
    fileCategoryIds: [],
    subFileCategoryIds: [],
    workflowStageIds: [],
    allocationOfficerIds: [],
    workflowTemplateStatusCodes: [],
    dateFrom: from,
    dateTo: to,
  };
}

function exportFilename(format: 'pdf' | 'excel'): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return format === 'pdf'
    ? `workflow-performance-report-${stamp}.pdf`
    : `workflow-performance-report-${stamp}.xlsx`;
}

export default function WorkflowPerformanceReportPage() {
  const { t } = useTranslation();
  const maxDate = todayIsoDate();
  const [draft, setDraft] = useState<Filters>(createEmptyFilters);
  const [appliedFilterIds, setAppliedFilterIds] = useState<WorkflowPerformanceReportFilters>(createEmptyFilterIds);
  const [rows, setRows] = useState<WorkflowPerformanceRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [resolvedSearchKey, setResolvedSearchKey] = useState<string | null>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const searchRequestKey = useMemo(
    () => `${searchTrigger}:${page}:${pageSize}:${JSON.stringify(appliedFilterIds)}`,
    [appliedFilterIds, page, pageSize, searchTrigger],
  );
  const loading = resolvedSearchKey !== searchRequestKey;

  const {
    workflowTemplateNames,
    workflowStageItems,
    allocationOfficerNames,
    fileCategoryNames,
    subFileCategoryNames,
    workflowTemplateStatusNames,
    mapWorkflowTemplateNamesToIds,
    mapWorkflowStageIds,
    mapAllocationOfficerNamesToIds,
    mapFileCategoryNamesToIds,
    mapSubFileCategoryNamesToIds,
    mapWorkflowTemplateStatusNamesToCodes,
    subFileCategoryDisabled,
    workflowStageDisabled,
    lookupsLoading,
    subLookupsLoading,
    workflowStagesLoading,
    allocationOfficersLoading,
  } = useWorkflowPerformanceLookups(
    draft.workflowTemplates,
    draft.fileCategories,
    draft.subFileCategories,
  );

  const buildFilterIds = useCallback(
    (source: Filters): WorkflowPerformanceReportFilters => ({
      workflowTemplateIds: mapWorkflowTemplateNamesToIds(source.workflowTemplates),
      fileCategoryIds: mapFileCategoryNamesToIds(source.fileCategories),
      subFileCategoryIds: mapSubFileCategoryNamesToIds(source.subFileCategories),
      workflowStageIds: mapWorkflowStageIds(source.workflowStageIds),
      allocationOfficerIds: mapAllocationOfficerNamesToIds(source.allocationOfficers),
      workflowTemplateStatusCodes: mapWorkflowTemplateStatusNamesToCodes(source.workflowTemplateStatuses),
      dateFrom: source.from,
      dateTo: source.to,
    }),
    [
      mapAllocationOfficerNamesToIds,
      mapFileCategoryNamesToIds,
      mapSubFileCategoryNamesToIds,
      mapWorkflowStageIds,
      mapWorkflowTemplateNamesToIds,
      mapWorkflowTemplateStatusNamesToCodes,
    ],
  );

  const validateDates = useCallback(
    (from: string, to: string) => {
      const err = validateReportDateRange(from, to);
      if (err) {
        setFlashToast({ message: t(`reports.dateValidation.${err}`), variant: 'error' });
        return false;
      }
      return true;
    },
    [t],
  );

  const runExport = useCallback(
    async (format: 'pdf' | 'excel', source: Filters) => {
      if (!validateDates(source.from, source.to)) return;
      const apiFormat = format === 'pdf' ? 'PDF' : 'EXCEL';
      const res = await exportWorkflowPerformanceReport(apiFormat, buildFilterIds(source));
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('reports.exportFailed'), variant: 'error' });
        throw new Error(res.message ?? 'export failed');
      }
      downloadReportBlob(res.data.blob, res.data.fileName ?? exportFilename(format));
      setFlashToast({ message: t('reports.generateSuccess'), variant: 'success' });
    },
    [buildFilterIds, t, validateDates],
  );

  useEffect(() => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    void searchWorkflowPerformanceReport(appliedFilterIds, page - 1, pageSize).then((res) => {
      if (controller.signal.aborted) return;
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('reports.searchFailed'), variant: 'error' });
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
        setResolvedSearchKey(searchRequestKey);
        return;
      }

      setRows(res.data.content);
      setTotalCount(res.data.totalElements);
      setTotalPages(Math.max(1, res.data.totalPages));
      setResolvedSearchKey(searchRequestKey);
    });

    return () => controller.abort();
  }, [appliedFilterIds, page, pageSize, searchRequestKey, t]);

  const columns = useMemo<Column<WorkflowPerformanceRow>[]>(
    () => [
      {
        key: 'workflowTemplateName',
        header: t('reports.wpr.colWorkflowTemplateName'),
        getValue: (r) => r.workflowTemplateName,
        render: (r) => <span className="rpt-td-name">{r.workflowTemplateName}</span>,
      },
      { key: 'fileCategory', header: t('reports.wpr.colFileCategory'), getValue: (r) => r.fileCategory },
      { key: 'subFileCategory', header: t('reports.wpr.colSubFileCategory'), getValue: (r) => r.subFileCategory },
      {
        key: 'workflowCode',
        header: t('reports.wpr.colWorkflowCode'),
        getValue: (r) => r.workflowCode,
        render: (r) => <span className="rpt-td-name">{r.workflowCode}</span>,
      },
      {
        key: 'workflowTemplateStatus',
        header: t('reports.wpr.colWorkflowTemplateStatus'),
        getValue: (r) => r.workflowTemplateStatus,
        render: (r) => <span className="rpt-td-name">{r.workflowTemplateStatus}</span>,
      },
      { key: 'allocationOfficer', header: t('reports.wpr.colAllocationOfficer'), getValue: (r) => r.allocationOfficer },
      { key: 'workflowStage', header: t('reports.wpr.colWorkflowStage'), getValue: (r) => r.workflowStage },
      {
        key: 'casesReceived',
        header: t('reports.wpr.colCasesReceived'),
        getValue: (r) => String(r.casesReceived),
        render: (r) => <ReportMetric value={r.casesReceived} variant="neutral" />,
      },
      {
        key: 'completed',
        header: t('reports.wpr.colCompleted'),
        getValue: (r) => String(r.completed),
        render: (r) => <ReportMetric value={r.completed} variant="green" />,
      },
      {
        key: 'inProgress',
        header: t('reports.wpr.colInProgress'),
        getValue: (r) => String(r.inProgress),
        render: (r) => <ReportMetric value={r.inProgress} variant="blue" />,
      },
      {
        key: 'overdue',
        header: t('reports.wpr.colOverdue'),
        getValue: (r) => String(r.overdue),
        render: (r) => <ReportMetric value={r.overdue} variant="orange" />,
      },
      {
        key: 'escalated',
        header: t('reports.wpr.colEscalated'),
        getValue: (r) => String(r.escalated),
        render: (r) => <ReportMetric value={r.escalated} variant="red" />,
      },
    ],
    [t],
  );

  return (
    <>
      <div className="bc">
        <Link to="/reports/workflow-performance">{t('reports.breadcrumb')}</Link>
        <span className="bc-sep">/</span>
        <span>{t('reports.wpr.title')}</span>
      </div>

      <div className="pg-title" style={{ marginBottom: 20 }}>
        {t('reports.wpr.title')}
      </div>

      <div className="flt-card rpt-filter-card">
        <div className="rpt-filter-grid-3">
          <ReportMultiSelect
            label={t('reports.filters.workflowTemplateNames')}
            value={draft.workflowTemplates}
            options={workflowTemplateNames}
            placeholder={t('reports.filters.selectWorkflowTemplates')}
            disabled={lookupsLoading}
            onChange={(workflowTemplates) =>
              setDraft((d) => ({ ...d, workflowTemplates, workflowStageIds: [], allocationOfficers: [] }))
            }
          />
          <ReportMultiSelect
            label={t('reports.filters.fileCategories')}
            value={draft.fileCategories}
            options={fileCategoryNames}
            placeholder={t('reports.filters.selectFileCategories')}
            disabled={lookupsLoading}
            onChange={(fileCategories) =>
              setDraft((d) => ({ ...d, fileCategories, subFileCategories: [], allocationOfficers: [] }))
            }
          />
          <ReportMultiSelect
            label={t('reports.filters.subFileCategories')}
            value={draft.subFileCategories}
            options={subFileCategoryNames}
            placeholder={t('reports.filters.selectSubFileCategories')}
            disabled={subFileCategoryDisabled || lookupsLoading || subLookupsLoading}
            onChange={(subFileCategories) => setDraft((d) => ({ ...d, subFileCategories, allocationOfficers: [] }))}
          />
        </div>
        <div className="rpt-filter-grid-3">
          <ReportMultiSelect
            label={t('reports.filters.workflowStages')}
            value={draft.workflowStageIds}
            optionItems={workflowStageItems}
            placeholder={t('reports.filters.selectWorkflowStages')}
            disabled={workflowStageDisabled || lookupsLoading || workflowStagesLoading}
            onChange={(workflowStageIds) => setDraft((d) => ({ ...d, workflowStageIds }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.allocationOfficer')}
            value={draft.allocationOfficers}
            options={allocationOfficerNames}
            placeholder={t('reports.filters.selectAllocationOfficer')}
            disabled={lookupsLoading || allocationOfficersLoading}
            onChange={(allocationOfficers) => setDraft((d) => ({ ...d, allocationOfficers }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.workflowTemplateStatus')}
            value={draft.workflowTemplateStatuses}
            options={workflowTemplateStatusNames}
            placeholder={t('reports.filters.selectWorkflowTemplateStatus')}
            searchable={false}
            onChange={(workflowTemplateStatuses) => setDraft((d) => ({ ...d, workflowTemplateStatuses }))}
          />
        </div>
        <div className="rpt-filter-grid-actions">
          <div className="flt-f">
            <div className="flt-l">{t('reports.filters.from')}</div>
            <FormattedDateInput
              className="flt-inp"
              value={draft.from}
              maxDate={draft.to || maxDate}
              onChange={(from) => setDraft((d) => ({ ...d, from }))}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('reports.filters.to')}</div>
            <FormattedDateInput
              className="flt-inp"
              value={draft.to}
              minDate={draft.from || undefined}
              maxDate={maxDate}
              onChange={(to) => setDraft((d) => ({ ...d, to }))}
            />
          </div>
          <ReportFilterActions
            onReset={() => {
              setDraft(createEmptyFilters());
              setAppliedFilterIds(createEmptyFilterIds());
              setPage(1);
              setSearchTrigger((n) => n + 1);
            }}
            onSearch={() => {
              if (!validateDates(draft.from, draft.to)) return;
              setAppliedFilterIds(buildFilterIds(draft));
              setPage(1);
              setSearchTrigger((n) => n + 1);
            }}
          />
        </div>
      </div>

      <DataTable
        title={t('reports.previewTitle')}
        toolbarRight={
          <ReportExportToolbar
            disabled={lookupsLoading}
            onGenerate={(format) => runExport(format, draft)}
          />
        }
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        enableHeaderSearch={false}
        enableSorting={false}
        emphasizedHeader
        rowsPerPageOptions={[10, 30, 50]}
        defaultRowsPerPage={10}
        footerCountNoun={t('reports.records')}
        serverPaging={{
          totalCount,
          totalPages,
          page,
          pageSize,
          onPageChange: (nextPage) => {
            setPage(nextPage);
          },
          onPageSizeChange: (nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          },
        }}
      />
    </>
  );
}
